import SearchableSelect from '@/components/SearchableSelect';
import { INVOICE_TYPES, coreFieldsError } from '@/lib/mandatoryFields';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import { soCreateAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ItemSearchSelect } from '@/components/ItemSearchSelect';
import { toast } from 'sonner';
import {
  Plus, Trash, MagnifyingGlass, FloppyDisk, CaretDown,
} from '@phosphor-icons/react';

const VISIT_TYPES = ['Clinic Visit', 'Home Visit'];

export default function CreateSalesOrderPage() {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [audiologists, setAudiologists] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [soNumber, setSoNumber] = useState('');
  const [soNumberManual, setSoNumberManual] = useState('');
  const [useManualSO, setUseManualSO] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    customer_id: '', customer_name: '', customer_phone: '', customer_state: '',
    branch_id: '', so_date: new Date().toISOString().split('T')[0],
    expected_shipping_date: '', audiologist_name: '', audiologist_employee_number: '',
    lead_source: '', visit_type: '', invoice_type: '', customer_notes: '', terms_and_conditions: '',
  });

  // Line items
  const [lineItems, setLineItems] = useState([
    { item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, _discountType: 'percent' }
  ]);

  // Customer search
  const [custSearch, setCustSearch] = useState('');
  const [showCustDropdown, setShowCustDropdown] = useState(false);

  // Load dropdowns
  useEffect(() => {
    const loadData = async () => {
      try {
        const [brRes, audRes, lsRes, itemsRes] = await Promise.allSettled([
          soCreateAPI.branches(),
          soCreateAPI.audiologists(),
          soCreateAPI.leadSources(),
          soCreateAPI.items({ per_page: 2000 }),
        ]);
        if (brRes.status === 'fulfilled') setBranches(brRes.value.data.branches || []);
        if (audRes.status === 'fulfilled') setAudiologists(audRes.value.data.audiologists || []);
        if (lsRes.status === 'fulfilled') setLeadSources(lsRes.value.data.lead_sources || []);
        if (itemsRes.status === 'fulfilled') setCatalogItems(itemsRes.value.data.items || []);
      } catch (e) { console.error(e); }
    };
    loadData();
  }, []);

  // Fetch next SO number when branch changes
  useEffect(() => {
    if (!form.branch_id || useManualSO) return;
    const branch = branches.find(b => b.id === form.branch_id);
    const sc = branch?.billing_state_code || branch?.state_code;
    if (!sc) return;
    soCreateAPI.nextSONumber(sc)
      .then(r => setSoNumber(r.data.next_so_number))
      .catch(() => {});
  }, [form.branch_id, branches, useManualSO]);

  // Customer search
  useEffect(() => {
    if (custSearch.length < 2) { setCustomers([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await soCreateAPI.customers({ search: custSearch, per_page: 10 });
        setCustomers(data.customers || []);
        setShowCustDropdown(true);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [custSearch]);

  const selectCustomer = (c) => {
    setForm(f => ({
      ...f,
      customer_id: c.id, customer_name: c.display_name || `${c.first_name} ${c.last_name}`.trim(),
      customer_phone: c.phone || '', customer_state: c.place_of_supply || '',
    }));
    setCustSearch(c.display_name || c.first_name);
    setShowCustDropdown(false);
  };

  const selectBranch = (branchId) => {
    setForm(f => ({ ...f, branch_id: branchId }));
  };

  const selectAudiologist = (empNum) => {
    const aud = audiologists.find(a => a.employee_number === empNum);
    setForm(f => ({
      ...f, audiologist_employee_number: empNum, audiologist_name: aud?.full_name || '',
    }));
  };

  // Line item management
  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };

  const selectCatalogItem = (idx, itemId) => {
    const item = catalogItems.find(i => i.id === itemId);
    if (!item) return;
    const gstRate = item.tax_preference === 'taxable' ? parseFloat(item.intra_state_gst || '18') : 0;
    setLineItems(prev => prev.map((li, i) => i === idx ? {
      ...li, item_id: item.id, name: item.name, product_code: item.product_code || '',
      hsn_sac: item.hsn_sac_code || '', rate: item.mrp || 0, tax_percent: gstRate,
    } : li));
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, _discountType: 'percent' }]);
  };

  const removeLineItem = (idx) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Calculate totals
  const calcLine = (li) => {
    const lineAmt = li.quantity * li.rate;
    const disc = li._discountType === 'amount' ? (parseFloat(li.discount_amount) || 0) : lineAmt * ((parseFloat(li.discount_percent) || 0) / 100);
    const taxable = lineAmt - disc;
    const tax = taxable * ((parseFloat(li.tax_percent) || 0) / 100);
    return { lineAmt, disc, taxable, tax, total: taxable + tax };
  };

  const totals = lineItems.reduce((acc, li) => {
    const c = calcLine(li);
    return { subtotal: acc.subtotal + c.lineAmt, discount: acc.discount + c.disc, tax: acc.tax + c.tax, grand: acc.grand + c.total };
  }, { subtotal: 0, discount: 0, tax: 0, grand: 0 });

  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSubmit = async () => {
    if (!form.branch_id) { toast.error('Branch/Location is required'); return; }
    const coreErr = coreFieldsError({ name: form.customer_name || custSearch, phone: form.customer_phone, leadSource: form.lead_source,
      audiologist: form.audiologist_employee_number || form.audiologist_name, invoiceType: form.invoice_type });
    if (coreErr) { toast.error(coreErr); return; }
    if (!lineItems.some(li => li.name)) { toast.error('At least one item is required'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        customer_name: form.customer_name || custSearch,
        so_number_manual: useManualSO ? soNumberManual : '',
        line_items: lineItems.filter(li => li.name).map(li => ({
          item_id: li.item_id, name: li.name, product_code: li.product_code,
          hsn_sac: li.hsn_sac, quantity: parseFloat(li.quantity) || 1,
          rate: parseFloat(li.rate) || 0,
          discount_percent: li._discountType === 'percent' ? (parseFloat(li.discount_percent) || 0) : 0,
          discount_amount: li._discountType === 'amount' ? (parseFloat(li.discount_amount) || 0) : 0,
          tax_percent: parseFloat(li.tax_percent) || 0,
        })),
      };
      const { data } = await soCreateAPI.create(payload);
      toast.success(`Sales Order ${data.so_number} created!`);
      navigate('/sales-credits');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create Sales Order');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="create-so-page">
      <PageHeader title="Create Sales Order" subtitle="Fill in the details to create a new sales order" />

      <div className="mt-6 space-y-6">
        {/* SO Number & Branch */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Branch/Location *</label>
              <SearchableSelect value={form.branch_id} onChange={selectBranch} placeholder="Select branch..." testid="so-branch" buttonClassName="w-full"
                  options={[...branches.map(b => ({ value: b.id, label: `${b.name} (${b.billing_state_code || b.state_code})` , keywords: b.code || b.clinic_code || "" }))]} />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">
                Sales Order #
                <button className="ml-2 text-[10px] text-blue-600 underline" onClick={() => setUseManualSO(!useManualSO)}>
                  {useManualSO ? 'Auto-generate' : 'Manual entry'}
                </button>
              </label>
              {useManualSO ? (
                <Input value={soNumberManual} onChange={e => setSoNumberManual(e.target.value)} placeholder="SO-DL-2627-XXXXX" data-testid="so-number-manual" />
              ) : (
                <Input value={soNumber} disabled className="bg-gray-50" data-testid="so-number-auto" />
              )}
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">SO Date</label>
              <Input type="date" value={form.so_date} onChange={e => setForm(f => ({ ...f, so_date: e.target.value }))} data-testid="so-date" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Expected Shipping Date</label>
              <Input type="date" value={form.expected_shipping_date} onChange={e => setForm(f => ({ ...f, expected_shipping_date: e.target.value }))} data-testid="so-ship-date" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Audiologist <span className="text-red-500">*</span></label>
              <SearchableSelect value={form.audiologist_employee_number} onChange={selectAudiologist} placeholder="Select audiologist..." testid="so-audiologist" buttonClassName="w-full"
                  options={[...audiologists.map(a => ({ value: a.employee_number, label: `${a.full_name} - ${a.location}` }))]} />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Lead Source <span className="text-red-500">*</span></label>
              <Select value={form.lead_source} onValueChange={v => setForm(f => ({ ...f, lead_source: v }))}>
                <SelectTrigger data-testid="so-lead-source"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {leadSources.map(ls => <SelectItem key={ls.id} value={ls.name}>{ls.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Invoice Type <span className="text-red-500">*</span></label>
              <SearchableSelect value={form.invoice_type} onChange={v => setForm(f => ({ ...f, invoice_type: v }))} placeholder="Select..." testid="so-invoice-type" buttonClassName="w-full"
                options={INVOICE_TYPES.map(t => ({ value: t, label: t }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Visit Type</label>
              <SearchableSelect value={form.visit_type} onChange={v => setForm(f => ({ ...f, visit_type: v }))} placeholder="Select..." testid="so-visit-type" buttonClassName="w-full"
                  options={[...VISIT_TYPES.map(v => ({ value: v, label: v }))]} />
            </div>
          </div>
        </div>

        {/* Customer */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Customer Details</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="relative col-span-2">
              <label className="text-xs text-[#64748B] block mb-1">Customer Name *</label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={custSearch}
                  onChange={e => { setCustSearch(e.target.value); setForm(f => ({ ...f, customer_name: e.target.value, customer_id: '' })); }}
                  placeholder="Search or type customer name..."
                  className="pl-9"
                  onFocus={() => customers.length > 0 && setShowCustDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCustDropdown(false), 200)}
                  data-testid="so-customer-name"
                />
                {showCustDropdown && customers.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 bg-white border border-[#E2E8F0] rounded-lg mt-1 shadow-lg max-h-48 overflow-y-auto">
                    {customers.map(c => (
                      <button key={c.id} onMouseDown={() => selectCustomer(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[#F8FAFC] flex justify-between" data-testid={`cust-option-${c.id}`}>
                        <span className="font-medium">{c.display_name}</span>
                        <span className="text-xs text-[#64748B]">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Phone <span className="text-red-500">*</span></label>
              <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} placeholder="+91..." data-testid="so-customer-phone" />
            </div>
          </div>
        </div>

        {/* Item Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Items</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="so-items-table">
              <thead>
                <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
                  <th className="text-left p-2 w-[250px]">Item</th>
                  <th className="text-left p-2 w-[80px]">Code</th>
                  <th className="text-center p-2 w-[60px]">Qty</th>
                  <th className="text-right p-2 w-[100px]">MRP</th>
                  <th className="text-right p-2 w-[140px]">Discount</th>
                  <th className="text-center p-2 w-[60px]">Tax %</th>
                  <th className="text-right p-2 w-[100px]">Amount</th>
                  <th className="p-2 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => {
                  const c = calcLine(li);
                  return (
                    <tr key={idx} className="border-t border-[#E2E8F0]" data-testid={`so-line-item-${idx}`}>
                      <td className="p-2">
                        <ItemSearchSelect
                          items={catalogItems}
                          value={li.item_id}
                          displayName={li.name}
                          onSelect={(v) => selectCatalogItem(idx, v)}
                          testId={`so-item-select-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-xs text-[#64748B]">{li.product_code || '-'}</td>
                      <td className="p-2">
                        <Input type="number" min="1" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} className="text-center h-8 text-xs" data-testid={`so-qty-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" value={li.rate} onChange={e => updateLineItem(idx, 'rate', e.target.value)} className="text-right h-8 text-xs" data-testid={`so-rate-${idx}`} />
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <Select value={li._discountType} onValueChange={v => updateLineItem(idx, '_discountType', v)}>
                            <SelectTrigger className="h-8 w-[52px] text-[10px] px-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percent">%</SelectItem>
                              <SelectItem value="amount">Rs</SelectItem>
                            </SelectContent>
                          </Select>
                          {li._discountType === 'percent' ? (
                            <Input type="number" min="0" max="100" value={li.discount_percent} onChange={e => updateLineItem(idx, 'discount_percent', e.target.value)} className="text-right h-8 text-xs w-[70px]" data-testid={`so-disc-${idx}`} />
                          ) : (
                            <Input type="number" min="0" value={li.discount_amount} onChange={e => updateLineItem(idx, 'discount_amount', e.target.value)} className="text-right h-8 text-xs w-[70px]" data-testid={`so-disc-amt-${idx}`} />
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        <Input type="number" value={li.tax_percent} onChange={e => updateLineItem(idx, 'tax_percent', e.target.value)} className="text-center h-8 text-xs" data-testid={`so-tax-${idx}`} />
                      </td>
                      <td className="p-2 text-right text-xs font-medium">{fmt(c.total)}</td>
                      <td className="p-2">
                        {lineItems.length > 1 && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeLineItem(idx)}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" onClick={addLineItem} data-testid="so-add-item">
            <Plus size={14} className="mr-1" /> Add Item
          </Button>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#64748B]">Subtotal</span><span>{fmt(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Discount</span><span className="text-red-600">-{fmt(totals.discount)}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Tax</span><span>{fmt(totals.tax)}</span></div>
              <div className="flex justify-between font-semibold border-t border-[#E2E8F0] pt-1 text-base">
                <span>Grand Total</span><span className="text-[#0F4C81]" data-testid="so-grand-total">{fmt(totals.grand)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes & T&C */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Customer Notes</label>
              <textarea
                value={form.customer_notes} onChange={e => setForm(f => ({ ...f, customer_notes: e.target.value }))}
                rows={3} className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                placeholder="Notes for the customer..." data-testid="so-notes"
              />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Terms & Conditions</label>
              <textarea
                value={form.terms_and_conditions} onChange={e => setForm(f => ({ ...f, terms_and_conditions: e.target.value }))}
                rows={3} className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                placeholder="Terms and conditions..." data-testid="so-terms"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate('/sales-credits')}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68] px-8" data-testid="so-submit-btn">
            <FloppyDisk size={16} className="mr-2" />{saving ? 'Creating...' : 'Create Sales Order'}
          </Button>
        </div>
      </div>
    </div>
  );
}
